import { useCallback, useEffect, useRef } from "react";

import { Placement } from "../../DashboardHeader/DashboardParams";
import { GRID_GAP } from "../sectionLayout";

/**
 * Edge-resize interaction for placed dashboard cards.
 *
 * A card carries an explicit `{row, col, w, h}` placement; dragging one of its
 * edge/corner handles grows it in whole grid cells. The RIGHT/BOTTOM edges
 * just grow `w`/`h`. The LEFT/TOP edges keep the opposite edge fixed — they
 * move the card's `col`/`row` AND grow, so the card extends toward the pointer
 * rather than only to the right/down. Like reorder, this owns no placement
 * state: it calls `place(id, next)` live so the URL-backed card list stays the
 * source of truth.
 *
 * Uses pointer events (not HTML5 DnD): the card itself is draggable for
 * reorder, so `isResizing()` lets the caller cancel a drag that a handle press
 * would otherwise start.
 *
 * `pointerup` ends the gesture where it stands (every step was already
 * committed live). `pointercancel` — the browser taking the pointer away
 * mid-gesture, e.g. a touch scroll takeover — ABORTS instead: the card is
 * put back at the placement the gesture started from, and the listeners
 * detach so a leaked gesture can never keep resizing the card.
 */

export type ResizeEdge =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

/** Every resize handle a placed card renders, edges then corners. */
export const RESIZE_EDGES: readonly ResizeEdge[] = [
  "left",
  "right",
  "top",
  "bottom",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];

interface ResizeState {
  id: string;
  edge: ResizeEdge;
  start: Placement;
  startX: number;
  startY: number;
  colStride: number; // px per column cell (width + gap)
  rowStride: number; // px per row cell (height + gap)
  capacity: number; // max columns, to clamp horizontal growth
}

export interface PlacedResize {
  /** True while a resize gesture is active — used to cancel a competing drag. */
  isResizing: () => boolean;
  /**
   * Begin resizing card `id` from `edge`; `placement` is its cell rect in the
   * coordinate space being EDITED (the master canvas). `strideCells` is the
   * card's RENDERED span, used to convert pointer pixels to cells — on a
   * compressed canvas the on-screen card can span fewer columns than its
   * master placement, and each rendered track crossed counts as one cell.
   * Defaults to the placement's own size.
   */
  startResize: (
    e: React.PointerEvent,
    id: string,
    edge: ResizeEdge,
    placement: Placement,
    capacity: number,
    strideCells?: { w: number; h: number },
  ) => void;
}

export function usePlacedResize(
  place: (id: string, placement: Placement) => void,
): PlacedResize {
  const stateRef = useRef<ResizeState | null>(null);
  // Keep the latest `place` reachable from the window listeners without
  // re-subscribing them mid-gesture.
  const placeRef = useRef(place);
  placeRef.current = place;
  // Detaches the active gesture's window listeners; null between gestures.
  const teardownRef = useRef<(() => void) | null>(null);

  const onMove = useCallback((e: PointerEvent) => {
    const s = stateRef.current;
    if (!s) {
      return;
    }
    const dCols = Math.round((e.clientX - s.startX) / s.colStride);
    const dRows = Math.round((e.clientY - s.startY) / s.rowStride);
    const { edge } = s;
    let { row, col, w, h } = s.start;
    // Corners include both a horizontal ("left"/"right") and a vertical
    // ("top"/"bottom") word, so applying each axis independently handles edges
    // AND corners with one code path.
    if (edge.includes("right")) {
      w = Math.max(
        1,
        Math.min(s.start.w + dCols, s.capacity - s.start.col + 1),
      );
    } else if (edge.includes("left")) {
      const rightEdge = s.start.col + s.start.w; // fixed
      col = Math.max(1, Math.min(s.start.col + dCols, rightEdge - 1));
      w = rightEdge - col;
    }
    if (edge.includes("bottom")) {
      h = Math.max(1, s.start.h + dRows);
    } else if (edge.includes("top")) {
      const bottomEdge = s.start.row + s.start.h; // fixed
      row = Math.max(1, Math.min(s.start.row + dRows, bottomEdge - 1));
      h = bottomEdge - row;
    }
    placeRef.current(s.id, { row, col, w, h });
  }, []);

  const startResize = useCallback(
    (
      e: React.PointerEvent,
      id: string,
      edge: ResizeEdge,
      placement: Placement,
      capacity: number,
      strideCells?: { w: number; h: number },
    ) => {
      e.preventDefault();
      e.stopPropagation();
      const cell = (e.currentTarget as HTMLElement).parentElement; // the cell
      if (!cell) {
        return;
      }
      const rect = cell.getBoundingClientRect();
      // rect = n*trackSize + (n-1)*gap  ⇒  stride (track + gap) = (rect + gap) / n
      // where n is the RENDERED span (strideCells), not the edited placement's.
      stateRef.current = {
        id,
        edge,
        start: placement,
        startX: e.clientX,
        startY: e.clientY,
        colStride: (rect.width + GRID_GAP) / (strideCells?.w ?? placement.w),
        rowStride: (rect.height + GRID_GAP) / (strideCells?.h ?? placement.h),
        capacity,
      };
      // One end handler for both terminations, so add/remove stay
      // symmetric: `pointerup` keeps the live-committed placement,
      // `pointercancel` (the browser took the pointer away mid-gesture)
      // aborts by restoring the placement the gesture started from.
      const onEnd = (endEvent: PointerEvent) => {
        const s = stateRef.current;
        if (s && endEvent.type === "pointercancel") {
          placeRef.current(s.id, s.start);
        }
        teardown();
      };
      const teardown = () => {
        stateRef.current = null;
        teardownRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onEnd);
        window.removeEventListener("pointercancel", onEnd);
      };
      teardownRef.current = teardown;
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onEnd);
      window.addEventListener("pointercancel", onEnd);
    },
    [onMove],
  );

  // Unmounting mid-gesture (e.g. navigating away with the pointer down) must
  // detach the window listeners too, or the leaked gesture would keep
  // resizing the card via the stale `place` callback.
  useEffect(() => () => teardownRef.current?.(), []);

  const isResizing = useCallback(() => stateRef.current !== null, []);

  return { isResizing, startResize };
}
